import React, { useState } from 'react';
import { Send, Mail, Phone, MapPin, Clock, Shield } from 'lucide-react';
import { supabase } from '../lib/supabase';
import SEOHead from '../components/SEOHead';

const COUNTRY_CODES = [
  { code: '+93', name: 'Afghanistan (+93)' },
  { code: '+355', name: 'Albania (+355)' },
  { code: '+213', name: 'Algeria (+213)' },
  { code: '+54', name: 'Argentina (+54)' },
  { code: '+61', name: 'Australia (+61)' },
  { code: '+43', name: 'Austria (+43)' },
  { code: '+973', name: 'Bahrain (+973)' },
  { code: '+880', name: 'Bangladesh (+880)' },
  { code: '+32', name: 'Belgium (+32)' },
  { code: '+55', name: 'Brazil (+55)' },
  { code: '+1', name: 'Canada (+1)' },
  { code: '+56', name: 'Chile (+56)' },
  { code: '+86', name: 'China (+86)' },
  { code: '+57', name: 'Colombia (+57)' },
  { code: '+420', name: 'Czech Republic (+420)' },
  { code: '+45', name: 'Denmark (+45)' },
  { code: '+20', name: 'Egypt (+20)' },
  { code: '+251', name: 'Ethiopia (+251)' },
  { code: '+358', name: 'Finland (+358)' },
  { code: '+33', name: 'France (+33)' },
  { code: '+49', name: 'Germany (+49)' },
  { code: '+233', name: 'Ghana (+233)' },
  { code: '+30', name: 'Greece (+30)' },
  { code: '+852', name: 'Hong Kong (+852)' },
  { code: '+36', name: 'Hungary (+36)' },
  { code: '+91', name: 'India (+91)' },
  { code: '+62', name: 'Indonesia (+62)' },
  { code: '+98', name: 'Iran (+98)' },
  { code: '+964', name: 'Iraq (+964)' },
  { code: '+353', name: 'Ireland (+353)' },
  { code: '+972', name: 'Israel (+972)' },
  { code: '+39', name: 'Italy (+39)' },
  { code: '+81', name: 'Japan (+81)' },
  { code: '+962', name: 'Jordan (+962)' },
  { code: '+254', name: 'Kenya (+254)' },
  { code: '+965', name: 'Kuwait (+965)' },
  { code: '+961', name: 'Lebanon (+961)' },
  { code: '+60', name: 'Malaysia (+60)' },
  { code: '+52', name: 'Mexico (+52)' },
  { code: '+212', name: 'Morocco (+212)' },
  { code: '+31', name: 'Netherlands (+31)' },
  { code: '+64', name: 'New Zealand (+64)' },
  { code: '+234', name: 'Nigeria (+234)' },
  { code: '+47', name: 'Norway (+47)' },
  { code: '+968', name: 'Oman (+968)' },
  { code: '+92', name: 'Pakistan (+92)' },
  { code: '+63', name: 'Philippines (+63)' },
  { code: '+48', name: 'Poland (+48)' },
  { code: '+351', name: 'Portugal (+351)' },
  { code: '+974', name: 'Qatar (+974)' },
  { code: '+40', name: 'Romania (+40)' },
  { code: '+7', name: 'Russia (+7)' },
  { code: '+966', name: 'Saudi Arabia (+966)' },
  { code: '+65', name: 'Singapore (+65)' },
  { code: '+27', name: 'South Africa (+27)' },
  { code: '+82', name: 'South Korea (+82)' },
  { code: '+34', name: 'Spain (+34)' },
  { code: '+94', name: 'Sri Lanka (+94)' },
  { code: '+46', name: 'Sweden (+46)' },
  { code: '+41', name: 'Switzerland (+41)' },
  { code: '+886', name: 'Taiwan (+886)' },
  { code: '+66', name: 'Thailand (+66)' },
  { code: '+90', name: 'Turkey (+90)' },
  { code: '+971', name: 'UAE (+971)' },
  { code: '+44', name: 'UK (+44)' },
  { code: '+380', name: 'Ukraine (+380)' },
  { code: '+1', name: 'USA (+1)' },
  { code: '+998', name: 'Uzbekistan (+998)' },
  { code: '+58', name: 'Venezuela (+58)' },
  { code: '+84', name: 'Vietnam (+84)' },
  { code: '+967', name: 'Yemen (+967)' },
  { code: '+263', name: 'Zimbabwe (+263)' },
];

const COUNTRIES = [
  'Afghanistan', 'Albania', 'Algeria', 'Argentina', 'Australia', 'Austria',
  'Bahrain', 'Bangladesh', 'Belarus', 'Belgium', 'Bolivia', 'Brazil', 'Bulgaria',
  'Cambodia', 'Canada', 'Chile', 'China', 'Colombia', 'Croatia', 'Cyprus',
  'Czech Republic', 'Denmark', 'Ecuador', 'Egypt', 'Estonia', 'Ethiopia',
  'Finland', 'France', 'Georgia', 'Germany', 'Ghana', 'Greece', 'Guatemala',
  'Hong Kong', 'Hungary', 'India', 'Indonesia', 'Iran', 'Iraq', 'Ireland',
  'Israel', 'Italy', 'Japan', 'Jordan', 'Kazakhstan', 'Kenya', 'Kuwait',
  'Latvia', 'Lebanon', 'Libya', 'Lithuania', 'Luxembourg', 'Malaysia',
  'Mexico', 'Morocco', 'Netherlands', 'New Zealand', 'Nigeria', 'Norway',
  'Oman', 'Pakistan', 'Panama', 'Peru', 'Philippines', 'Poland', 'Portugal',
  'Qatar', 'Romania', 'Russia', 'Saudi Arabia', 'Serbia', 'Singapore',
  'Slovakia', 'South Africa', 'South Korea', 'Spain', 'Sri Lanka', 'Sweden',
  'Switzerland', 'Taiwan', 'Tanzania', 'Thailand', 'Tunisia', 'Turkey',
  'UAE', 'Uganda', 'UK', 'Ukraine', 'USA', 'Uzbekistan', 'Venezuela',
  'Vietnam', 'Yemen', 'Zimbabwe',
];

interface FormData {
  name: string;
  email: string;
  phone_country_code: string;
  phone: string;
  company: string;
  country: string;
  subject: string;
  message: string;
}

const Contact = () => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone_country_code: '+91',
    phone: '',
    company: '',
    country: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{[key: string]: string}>({});

  const validateField = (name: string, value: string) => {
    const errors: {[key: string]: string} = {};
    
    switch (name) {
      case 'name':
        if (!value.trim()) errors.name = 'Name is required';
        break;
      case 'email':
        if (!value.trim()) {
          errors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          errors.email = 'Please enter a valid email address';
        }
        break;
      case 'subject':
        if (!value.trim()) errors.subject = 'Subject is required';
        break;
      case 'message':
        if (!value.trim()) errors.message = 'Message is required';
        break;
    }
    
    setValidationErrors(prev => ({
      ...prev,
      [name]: errors[name] || ''
    }));
    
    return !errors[name];
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);

    // Validate all required fields
    const requiredFields = ['name', 'email', 'subject', 'message'];
    let isValid = true;
    
    requiredFields.forEach(field => {
      if (!validateField(field, formData[field as keyof FormData])) {
        isValid = false;
      }
    });

    if (!isValid) {
      setIsSubmitting(false);
      return;
    }

    try {
      // Save directly to Supabase (webhook will trigger email via GitHub Actions)
      const { error } = await supabase
        .from('contact_submissions')
        .insert([formData]);

      if (error) {
        throw error;
      }

      // Google Ads conversion tracking
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'ads_conversion_SUBMIT_LEAD_FORM_1', {});
      }

      // Reset form after successful submission
      setFormData({
        name: '',
        email: '',
        phone_country_code: '+91',
        phone: '',
        company: '',
        country: '',
        subject: '',
        message: '',
      });
      setValidationErrors({});
      setSubmitSuccess(true);

      // Hide success message after 5 seconds
      setTimeout(() => setSubmitSuccess(false), 5000);
    } catch (error) {
      setSubmitError('There was an error submitting your message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear validation error when user starts typing
    if (validationErrors[name]) {
      validateField(name, value);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    validateField(name, value);
  };

  return (
    <>
    <SEOHead
      title="Contact Agility | AI Consulting & Solutions"
      description="Get in touch with Agility for AI consulting, automation, and data engineering solutions. Schedule a free consultation."
      url="https://www.agilitytech.ai/contact"
    />
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-r from-blue-900 to-black overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&q=80"
            alt="Contact Banner"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-transparent" />
        </div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Get in Touch
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              Ready to transform your business with AI? Contact us today to discuss how we can help you achieve your goals.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Form */}
            <div className="lg:col-span-2">
              {submitError && (
                <div className="mb-6 p-4 bg-red-900/50 border border-red-500/50 text-red-400 rounded-lg">
                  {submitError}
                </div>
              )}
              
              {submitSuccess && (
                <div className="success-message mb-6 p-6 bg-green-900/50 border border-green-500/50 text-green-400 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                      <Send className="h-3 w-3 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold">Message Sent Successfully!</h3>
                      <p className="text-sm text-green-300">Thank you for your message. We'll get back to you within 24 hours.</p>
                    </div>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="form-field">
                    <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                      Full Name *
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={`w-full px-4 py-3 rounded-lg bg-gray-900/50 border transition-all duration-300
                          text-white placeholder-gray-500 pr-10 ${
                          validationErrors.name 
                            ? 'border-red-500 focus:ring-2 focus:ring-red-400' 
                            : formData.name && !validationErrors.name
                            ? 'border-green-500 focus:ring-2 focus:ring-green-400'
                            : 'border-gray-800/50 focus:ring-2 focus:ring-teal-400 focus:border-transparent'
                        }`}
                        placeholder="John Doe"
                      />
                      {formData.name && !validationErrors.name && (
                        <div className="validation-icon absolute right-3 top-1/2 transform -translate-y-1/2">
                          <svg className="h-5 w-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      )}
                      {validationErrors.name && (
                        <div className="validation-icon absolute right-3 top-1/2 transform -translate-y-1/2">
                          <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </div>
                      )}
                    </div>
                    {validationErrors.name && (
                      <p className="error-message text-red-400 text-sm mt-2">{validationErrors.name}</p>
                    )}
                  </div>
                  
                  <div className="form-field">
                    <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                      Email Address *
                    </label>
                    <div className="relative">
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={`w-full px-4 py-3 rounded-lg bg-gray-900/50 border transition-all duration-300
                          text-white placeholder-gray-500 pr-10 ${
                          validationErrors.email 
                            ? 'border-red-500 focus:ring-2 focus:ring-red-400' 
                            : formData.email && !validationErrors.email
                            ? 'border-green-500 focus:ring-2 focus:ring-green-400'
                            : 'border-gray-800/50 focus:ring-2 focus:ring-teal-400 focus:border-transparent'
                        }`}
                        placeholder="john@example.com"
                      />
                      {formData.email && !validationErrors.email && (
                        <div className="validation-icon absolute right-3 top-1/2 transform -translate-y-1/2">
                          <svg className="h-5 w-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      )}
                      {validationErrors.email && (
                        <div className="validation-icon absolute right-3 top-1/2 transform -translate-y-1/2">
                          <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </div>
                      )}
                    </div>
                    {validationErrors.email && (
                      <p className="error-message text-red-400 text-sm mt-2">{validationErrors.email}</p>
                    )}
                  </div>
                  
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-300 mb-2">
                      Phone Number
                    </label>
                    <div className="flex">
                      <select
                        id="phone_country_code"
                        name="phone_country_code"
                        value={formData.phone_country_code}
                        onChange={handleChange}
                        className="w-36 flex-shrink-0 px-2 py-3 rounded-l-lg bg-gray-900/50 border border-r-0 border-gray-800/50
                          focus:ring-2 focus:ring-teal-400 focus:border-transparent transition-all duration-300
                          text-white text-sm appearance-none cursor-pointer"
                      >
                        {COUNTRY_CODES.map((cc) => (
                          <option key={cc.code + cc.name} value={cc.code} className="bg-gray-900 text-white">
                            {cc.name}
                          </option>
                        ))}
                      </select>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="flex-1 min-w-0 px-4 py-3 rounded-r-lg bg-gray-900/50 border border-gray-800/50
                          focus:ring-2 focus:ring-teal-400 focus:border-transparent transition-all duration-300
                          text-white placeholder-gray-500"
                        placeholder="98765 43210"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="company" className="block text-sm font-medium text-gray-300 mb-2">
                      Company/Organization
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg bg-gray-900/50 border border-gray-800/50
                        focus:ring-2 focus:ring-teal-400 focus:border-transparent transition-all duration-300
                        text-white placeholder-gray-500"
                      placeholder="Your Company Name"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label htmlFor="country" className="block text-sm font-medium text-gray-300 mb-2">
                      Country of Residence
                    </label>
                    <select
                      id="country"
                      name="country"
                      value={formData.country}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg bg-gray-900/50 border border-gray-800/50
                        focus:ring-2 focus:ring-teal-400 focus:border-transparent transition-all duration-300
                        text-white appearance-none cursor-pointer"
                    >
                      <option value="" className="bg-gray-900 text-gray-500">Select your country</option>
                      {COUNTRIES.map((c) => (
                        <option key={c} value={c} className="bg-gray-900 text-white">{c}</option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <div className="form-field">
                  <label htmlFor="subject" className="block text-sm font-medium text-gray-300 mb-2">
                    Subject *
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      required
                      value={formData.subject}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={`w-full px-4 py-3 rounded-lg bg-gray-900/50 border transition-all duration-300
                        text-white placeholder-gray-500 pr-10 ${
                        validationErrors.subject 
                          ? 'border-red-500 focus:ring-2 focus:ring-red-400' 
                          : formData.subject && !validationErrors.subject
                          ? 'border-green-500 focus:ring-2 focus:ring-green-400'
                          : 'border-gray-800/50 focus:ring-2 focus:ring-teal-400 focus:border-transparent'
                      }`}
                      placeholder="How can we help you?"
                    />
                    {formData.subject && !validationErrors.subject && (
                      <div className="validation-icon absolute right-3 top-1/2 transform -translate-y-1/2">
                        <svg className="h-5 w-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}
                    {validationErrors.subject && (
                      <div className="validation-icon absolute right-3 top-1/2 transform -translate-y-1/2">
                        <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}
                  </div>
                  {validationErrors.subject && (
                    <p className="error-message text-red-400 text-sm mt-2">{validationErrors.subject}</p>
                  )}
                </div>
                
                <div className="form-field">
                  <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-2">
                    Message *
                  </label>
                  <div className="relative">
                    <textarea
                      id="message"
                      name="message"
                      required
                      value={formData.message}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      rows={6}
                      className={`w-full px-4 py-3 rounded-lg bg-gray-900/50 border transition-all duration-300
                        text-white placeholder-gray-500 ${
                        validationErrors.message 
                          ? 'border-red-500 focus:ring-2 focus:ring-red-400' 
                          : formData.message && !validationErrors.message
                          ? 'border-green-500 focus:ring-2 focus:ring-green-400'
                          : 'border-gray-800/50 focus:ring-2 focus:ring-teal-400 focus:border-transparent'
                      }`}
                      placeholder="Please describe your project or inquiry..."
                    />
                  </div>
                  {validationErrors.message && (
                    <p className="error-message text-red-400 text-sm mt-2">{validationErrors.message}</p>
                  )}
                </div>

                <div className="response-time-info text-center py-4">
                  <p className="text-gray-400 text-sm">
                    <Clock className="inline h-4 w-4 mr-2" />
                    We typically respond within 24 hours during business days.
                  </p>
                </div>

                <div className="privacy-policy-link text-center">
                  <p className="text-gray-400 text-sm">
                    By submitting this form, you agree to our{' '}
                    <a href="/privacy-policy" className="text-teal-400 hover:text-teal-300 underline transition-colors">
                      Privacy Policy
                    </a>
                  </p>
                </div>
                
                <div>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`bg-teal-500 text-white px-8 py-4 rounded-lg text-lg font-medium 
                      transition-all duration-300 flex items-center space-x-3 
                      group shadow-lg ${isSubmitting ? 'opacity-75 cursor-not-allowed' : 'hover:bg-teal-400'}`}
                  >
                    <span>{isSubmitting ? 'Sending...' : 'Send Message'}</span>
                    <Send size={20} className="group-hover:translate-x-1 transition-transform" />
                  </button>
                </div>
              </form>
            </div>

            {/* Contact Information Sidebar */}
            <div className="contact-info-sidebar">
              <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-8 border border-gray-800/50">
                <h3 className="text-2xl font-bold text-white mb-6">Alternative Contact Methods</h3>
                
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 bg-teal-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Mail className="h-5 w-5 text-teal-400" />
                    </div>
                    <div>
                      <h4 className="text-white font-semibold mb-1">Email</h4>
                      <a href="mailto:hello@agilitytech.ai" className="text-gray-300 hover:text-teal-400 transition-colors">
                        hello@agilitytech.ai
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 bg-teal-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Phone className="h-5 w-5 text-teal-400" />
                    </div>
                    <div>
                      <h4 className="text-white font-semibold mb-1">Phone</h4>
                      <a href="tel:+917041402034" className="text-gray-300 hover:text-teal-400 transition-colors">
                        +91 7041402034
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 bg-teal-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin className="h-5 w-5 text-teal-400" />
                    </div>
                    <div>
                      <h4 className="text-white font-semibold mb-1">Headquarters</h4>
                      <p className="text-gray-300">Ahmedabad, India</p>
                      <p className="text-gray-400 text-sm">Global offices in London & New York</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 bg-teal-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Clock className="h-5 w-5 text-teal-400" />
                    </div>
                    <div>
                      <h4 className="text-white font-semibold mb-1">Business Hours</h4>
                      <p className="text-gray-300">9:00 AM - 12:00 AM (IST)</p>
                      <p className="text-gray-400 text-sm">24/7 Global Support Available</p>
                    </div>
                  </div>
                </div>

                <div className="mt-8 pt-8 border-t border-gray-700">
                  <h4 className="text-white font-semibold mb-4">Follow Us</h4>
                  <div className="flex space-x-4">
                    <a
                      href="https://www.linkedin.com/company/agilitytech/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 bg-teal-500/10 rounded-lg flex items-center justify-center
                        hover:bg-teal-500/20 transition-colors"
                    >
                      <svg className="h-5 w-5 text-teal-400" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                      </svg>
                    </a>
                    <a 
                      href="https://twitter.com/agilitytech_ai" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="w-10 h-10 bg-teal-500/10 rounded-lg flex items-center justify-center 
                        hover:bg-teal-500/20 transition-colors"
                    >
                      <svg className="h-5 w-5 text-teal-400" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                      </svg>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default Contact;